import { useState, useMemo, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { FolderCrudModals } from "../FolderCrudModals/FolderCrudModals";
// import { AddEntityButton } from "../../../components/AddEntityButton/AddEntityButton";
// import { strings } from "../../../config/string";
import { FolderItem } from "../FolderItem/FolderItem";
import { FolderForeignIdMapModal } from "../../../components/modals/FolderForeignIdMapModal";
import { EmptyListComponent } from "../../../components/EmptyListComponent/EmptyListComponent";
import debounce from 'lodash/debounce';
import { Folder, Office } from "../../../types";
import { useGetAllOffices } from "../../../services/officeService";

type Props = {
  folders: Folder[];
  officeId?: string;
  organisationId?: string;
};

export const FoldersContainer = ({ folders = [], officeId: initialOfficeId }: Props) => {
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showForeignIdModal, setShowForeignIdModal] = useState(false);
  const [folderId, setFolderId] = useState<string>();

  const { data: allOffices = [] } = useGetAllOffices();

  const [selectedOfficeId, setSelectedOfficeId] = useState<string>(initialOfficeId || "");
  const [filterInput, setFilterInput] = useState('');
  const [debouncedFilterInput, setDebouncedFilterInput] = useState('');

  useEffect(() => {
    setSelectedOfficeId(initialOfficeId || "");
  }, [initialOfficeId]);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedFilterInput(filterInput);
    }, 300);

    handler();

    return () => {
      handler.cancel();
    };
  }, [filterInput]);

  const filteredFolders = useMemo(() => {
    let tempFolders = folders;

    if (selectedOfficeId) {
      tempFolders = tempFolders.filter((folder: Folder) => folder.officeId === selectedOfficeId);
    }

    if (debouncedFilterInput) {
      const lowerCaseFilter = debouncedFilterInput.toLowerCase();
      tempFolders = tempFolders.filter((folder: Folder) =>
        folder.name.toLowerCase().includes(lowerCaseFilter) ||
        (folder.officeName && folder.officeName.toLowerCase().includes(lowerCaseFilter)) ||
        (folder.foreignId && folder.foreignId.toString().includes(lowerCaseFilter))
      );
    }

    return tempFolders;
  }, [folders, selectedOfficeId, debouncedFilterInput]);

  const clearFilters = () => {
    setSelectedOfficeId("");
    setFilterInput("");
  };

  const handleFilterByOffice = (officeId: string) => {
    setSelectedOfficeId(officeId);
  };

  return (
    <div>
      <Row className="mb-3 g-3 align-items-center p-3 bg-light rounded shadow-sm">
        <Col xs={12} md={4}>
          <Form.Group controlId="folderOfficeFilter">
            <Form.Label className="text-dark fw-semibold small mb-1">Filter by Office</Form.Label>
            <Form.Select
              aria-label="Filter by Office"
              value={selectedOfficeId}
              onChange={(e) => setSelectedOfficeId(e.target.value)}
              className="form-select-sm"
            >
              <option value="">All Offices</option>
              {allOffices.map((office: Office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={5}>
          <Form.Group controlId="folderTextFilter">
            <Form.Label className="text-dark fw-semibold small mb-1">Search Folders</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search name, office, ID..."
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              className="form-control-sm bg-white text-dark"
            />
          </Form.Group>
        </Col>

        {/*<Col xs={12} md={3} className="text-md-end align-self-end d-flex justify-content-end gap-2">*/}
        {/*  <Button variant="outline-secondary" size="sm" onClick={clearFilters} title="Clear filters" disabled={!selectedOfficeId && !filterInput}>*/}
        {/*    Clear*/}
        {/*  </Button>*/}
        {/*  <AddEntityButton*/}
        {/*    onClick={() => setShowAdd(true)}*/}
        {/*    text={strings.addNewFolder}*/}
        {/*  />*/}
        {/*</Col>*/}
      </Row>

      <FolderCrudModals
        showAdd={showAdd}
        showDelete={showDelete}
        showEdit={showEdit}
        folderId={folderId}
        officeId={selectedOfficeId || initialOfficeId}
        onAddClose={() => setShowAdd(false)}
        onDeleteClose={() => { setShowDelete(false); setFolderId(undefined); }}
        onEditClose={() => { setShowEdit(false); setFolderId(undefined); }}
      />

      <Row xs={1} sm={2} lg={3} xl={4} className="mt-1">
        {filteredFolders.length > 0 ? (
          filteredFolders.map((folder) => (
            <FolderItem
              key={folder.id}
              folder={folder}
              onClickEdit={() => {
                setFolderId(folder.id);
                setShowEdit(true);
              }}
              onClickDelete={() => {
                setFolderId(folder.id);
                setShowDelete(true);
              }}
              onFilterByOffice={handleFilterByOffice}
            />
          ))
        ) : (
          <Col xs={12}>
            <EmptyListComponent 
              message={folders.length === 0 ? "No folders available" : "No folders found matching your criteria"}
              filterApplied={!!(selectedOfficeId || debouncedFilterInput)}
              onClearFilter={clearFilters}
            />
          </Col>
        )}
      </Row>

      {folderId && (
        <FolderForeignIdMapModal
          folderId={folderId}
          open={showForeignIdModal}
          onClose={() => { setShowForeignIdModal(false); setFolderId(undefined); }}
        />
      )}
    </div>
  );
};
